<script setup lang="ts">
import { computed, ref, markRaw } from 'vue'
import { useTicketStore, type Ticket } from '@/stores/useTicketStore'
import { CheckCircleIcon, PaperClipIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import TicketResolveModal from './TicketResolveModal.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'

const ticketStore = useTicketStore()
const isResolveModalOpen = ref(false)

const ticket = computed<Ticket | null>(() => ticketStore.ticket)

function toMs(date: number): number {
  return date < 10000000000 ? date * 1000 : date
}

const commentsAsc = computed(() => {
  if (!ticket.value) return []
  const comments = [...(ticket.value.comments || [])]
  return comments.sort((a, b) => toMs(a.date ?? 0) - toMs(b.date ?? 0))
})

const newComment = ref('')
const files = ref<File[]>([])
const adding = computed(() => ticketStore.loading)

async function submitComment() {
  if (!ticket.value?.uuid) return
  const content = newComment.value.trim()
  if (!content) return
  await ticketStore.addComment(ticket.value.uuid, content, files.value)
  newComment.value = ''
  files.value = []
}

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files) {
    const newFiles = Array.from(target.files)
    files.value = [...files.value, ...newFiles]
    target.value = ''
  }
}

function removeFile(index: number) {
  files.value.splice(index, 1)
}

const formatDate = (timestamp: number) => {
  if (!timestamp) return '-'
  // Handle both seconds and milliseconds
  const date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp)
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const checkCircleIcon = markRaw(CheckCircleIcon)
const paperClipIcon = markRaw(PaperClipIcon)
const xMarkIcon = markRaw(XMarkIcon)

const statusTranslations: Record<string, string> = {
  'NEW': 'NOUVEAU',
  'OPEN': 'OUVERT',
  'WAITING': 'EN ATTENTE',
  'PAUSED': 'EN PAUSE',
  'RESOLVED': 'RÉSOLU',
  'CLOSED': 'FERMÉ'
}

const statusColors: Record<string, string> = {
  'NEW': 'bg-blue-100 text-blue-800 border-blue-200',
  'OPEN': 'bg-green-100 text-green-800 border-green-200',
  'WAITING': 'bg-orange-100 text-orange-800 border-orange-200',
  'PAUSED': 'bg-gray-100 text-gray-800 border-gray-200',
  'CLOSED': 'bg-red-100 text-red-800 border-red-200',
  'RESOLVED': 'bg-teal-100 text-teal-800 border-teal-200'
}

function openResolveModal() {
  isResolveModalOpen.value = true
}

function closeResolveModal() {
  isResolveModalOpen.value = false
}

async function handleResolve(comment: string) {
  if (!ticket.value?.uuid) return
  await ticketStore.resolveTicket(ticket.value.uuid, comment)
  closeResolveModal()
}
</script>

<template>
  <div v-if="ticket" class="flex flex-col lg:flex-row gap-4 p-4 h-[calc(100vh-140px)] overflow-hidden">
    <!-- Middle Column: Title and Comments -->
    <div class="flex-1 flex flex-col gap-4 min-h-0">
      <div class="bg-white rounded-lg border border-gray-200 p-4 shadow-sm shrink-0">
        <h1 class="text-lg font-semibold text-gray-900">{{ ticket.title }}</h1>
      </div>

      <!-- Comments Section -->
      <div class="flex-1 flex flex-col min-h-0 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <!-- Scrollable Comments List -->
        <div class="flex-1 overflow-y-auto pr-2 min-h-0">
          <div v-if="commentsAsc.length > 0" class="flex flex-col gap-4">
            <div
              v-for="comment in commentsAsc"
              :key="comment.id"
              class="flex flex-col"
              :class="comment.supportComment ? 'items-start' : 'items-end'"
            >
              <div
                class="max-w-[80%] rounded-lg shadow-sm border"
                :class="[
                  comment.supportComment
                    ? 'bg-primary border-primary text-primary-content'
                    : 'bg-white border-gray-200 text-gray-800'
                ]"
              >
                <div class="p-4">
                    <div
                    class="flex items-start justify-between mb-2 pb-2 border-b"
                    :class="comment.supportComment ? 'border-primary-focus/20' : 'border-gray-400'"
                  >
                    <div class="flex flex-col gap-0.5 mr-2">
                      <span class="font-semibold"
                            :class="comment.supportComment ? 'text-primary-content' : 'text-gray-900'">
                        {{ comment.author }}
                      </span>
                      <span v-if="comment.authorDetail"
                            class="text-xs italic"
                            :class="comment.supportComment ? 'text-primary-content/80' : 'text-gray-500'">
                        {{ comment.authorDetail }}
                      </span>
                    </div>
                    <span class="text-xs shrink-0 pt-0.5" :class="comment.supportComment ? 'text-primary-content/80' : 'text-gray-500'">
                      {{ formatDate(comment.date) }}
                    </span>
                  </div>
                  <div class="prose max-w-none whitespace-pre-wrap"
                       :class="comment.supportComment ? 'text-primary-content' : 'text-gray-800'">
                    {{ comment.comment }}
                  </div>

                  <!-- Comment Attachments -->
                  <div v-if="comment.attachments && comment.attachments.length > 0" class="mt-3 flex flex-wrap gap-2">
                    <a
                      v-for="file in comment.attachments"
                      :key="file.id"
                      :href="file.url"
                      target="_blank"
                      class="flex items-center gap-2 px-2 py-1 rounded border text-xs transition-colors"
                      :class="comment.supportComment
                        ? 'bg-primary-focus/20 border-primary-focus/30 text-primary-content hover:bg-primary-focus/40'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'"
                    >
                      <component :is="paperClipIcon" class="w-3 h-3" />
                      <span class="truncate max-w-[150px]">{{ file.filename || file.name }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
            Aucun commentaire pour le moment.
          </div>
        </div>

        <!-- Add Comment form -->
        <div v-if="ticket.ticketStatus !== 'RESOLVED' && ticket.ticketStatus !== 'CLOSED'"
             class="border-t border-gray-400 pt-4 mt-4 shrink-0">
          <textarea
            v-model="newComment"
            class="textarea textarea-bordered w-full h-24 focus:ring-1 focus:ring-primary"
            placeholder="Écrire un commentaire..."
            :disabled="adding"
          />

          <!-- File Attachments List -->
          <div v-if="files.length > 0" class="flex flex-wrap gap-2 mt-2">
            <div
              v-for="(file, index) in files"
              :key="index"
              class="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-md border border-gray-200 text-xs"
            >
              <span class="max-w-[150px] truncate">{{ file.name }}</span>
              <button
                @click="removeFile(index)"
                class="text-gray-400 hover:text-error transition-colors"
                title="Supprimer"
              >
                <component :is="xMarkIcon" class="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div class="flex justify-between items-center mt-2">
            <div>
              <input
                type="file"
                id="comment-files"
                class="hidden"
                multiple
                @change="handleFileChange"
              />
              <label
                for="comment-files"
                class="btn btn-ghost btn-sm gap-2 text-gray-500 hover:text-primary border-gray-200"
                :class="{ 'opacity-50 pointer-events-none': adding }"
              >
                <component :is="paperClipIcon" class="w-4 h-4" />
                <span class="hidden sm:inline">Joindre des fichiers</span>
              </label>
            </div>
            <button
              class="btn btn-primary btn-sm"
              @click="submitComment"
              :disabled="!newComment.trim() || adding"
            >
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Metadata and Actions -->
    <div class="w-full lg:w-80 flex flex-col gap-4 shrink-0 overflow-y-auto">
      <div class="bg-white rounded-lg border border-gray-200 p-5 shadow-sm space-y-4">
        <h3 class="text-sm font-bold text-gray-500 uppercase tracking-wider">Détails du Ticket</h3>

        <div class="space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">ID Ticket</label>
            <div class="text-sm font-mono font-semibold rounded">#{{ ticket.internalReference }}</div>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Statut</label>
            <div
              :class="['inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold', statusColors[ticket.ticketStatus] || 'bg-gray-100 text-gray-800']">
              {{ statusTranslations[ticket.ticketStatus] || ticket.ticketStatus }}
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">Créé le</label>
            <div class="text-sm text-gray-900">{{ formatDate(ticket.dateCreated || ticket.createdAt) }}</div>
          </div>
        </div>

        <div v-if="ticket.ticketStatus !== 'RESOLVED' && ticket.ticketStatus !== 'CLOSED'"
             class="pt-4 border-t border-gray-400">
          <ButtonDaisy
            text="Ticket résolu"
            :variant="'primary'"
            :icon="checkCircleIcon"
            class="w-full"
            @click="openResolveModal"
            :disabled="ticketStore.loading"
          />
        </div>
      </div>
    </div>

    <TicketResolveModal
      :isOpen="isResolveModalOpen"
      @close="closeResolveModal"
      @resolve="handleResolve"
    />
  </div>

  <div v-else class="flex flex-col items-center justify-center p-12 text-gray-500">
    <div class="loading loading-spinner loading-lg mb-4 text-primary"></div>
    <p class="text-lg">Chargement du ticket...</p>
  </div>
</template>

<style scoped>
.prose {
  font-size: 0.9375rem;
  line-height: 1.5;
}
</style>
